// ————————————————————————————————————————————— LIB. ————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

import { gsap } from "gsap";

// ———————————————————————————————————————————— UTIL. ————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

// Configuration
const CONFIG = {
  TOOLTIP_SHOW_DELAY: 200, // Delay before showing tooltip (ms)
  TOOLTIP_HIDE_DELAY: 50, // Delay before hiding tooltip (ms)
  MOUSE_OFFSET_Y: 20, // Tooltip offset above cursor (px)
  ANIMATION_DURATION: 0.3, // Show/hide animation duration (s)
  FOLLOW_DURATION: 0.1, // Mouse follow animation duration (s)
};

/**
 * Hide all tooltips except the specified one
 */
function hideAllTooltipsExcept(exceptTooltip = null) {
  const allTooltips = document.querySelectorAll("[data-tooltip]");

  allTooltips.forEach((tooltip) => {
    if (tooltip !== exceptTooltip) {
      gsap.to(tooltip, { opacity: 0, duration: 0.15, ease: "power2.in" });
    }
  });
}

// ———————————————————————————————————————— EVENT HANDLERS ———————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

/**
 * Handle mouse enter on circle - position and show tooltip
 */
function handleCircleMouseEnter(event) {
  const circle = event.target;
  const circleId = circle.dataset.id;

  if (!circleId) return;
  // console.log("mouse entered circle", circleId);

  // Find matching tooltip
  const tooltip = document.querySelector(`[data-tooltip][data-id="${circleId}"]`);
  if (!tooltip) return;

  // Clear any pending hide timeout
  if (hideTooltipTimeout) {
    clearTimeout(hideTooltipTimeout);
    hideTooltipTimeout = null;
  }

  // Set active tooltip for global mouse tracking immediately
  activeTooltip = tooltip;
  activeCircleId = circleId;
  isHoveringCircle = true;

  // Hide all other tooltips immediately
  hideAllTooltipsExcept(tooltip);

  // Clear any existing timeouts
  if (showTooltipTimeout) {
    clearTimeout(showTooltipTimeout);
    showTooltipTimeout = null;
  }

  // Position tooltip at mouse position (but keep it hidden)
  const mouseX = event.clientX;
  const mouseY = event.clientY - CONFIG.MOUSE_OFFSET_Y;

  gsap.set(tooltip, {
    position: "fixed",
    left: mouseX,
    top: mouseY,
    xPercent: -50,
    yPercent: -100,
    zIndex: 1000,
    opacity: 0,
  });

  // Delay showing the tooltip
  showTooltipTimeout = setTimeout(() => {
    // Only show if still hovering the same circle
    if (activeTooltip === tooltip && activeCircleId === circleId && isHoveringCircle) {
      gsap.to(tooltip, {
        opacity: 1,
        duration: CONFIG.ANIMATION_DURATION,
        ease: "back.out(1.7)",
      });
    }
    showTooltipTimeout = null;
  }, CONFIG.TOOLTIP_SHOW_DELAY);
}

/**
 * Global state management for tooltips
 */
let activeTooltip = null;
let activeCircleId = null;
let isHoveringCircle = false;
let showTooltipTimeout = null;
let hideTooltipTimeout = null;

/**
 * Global mouse move handler for tooltip following
 */

function handleGlobalMouseMove(event) {
  if (!activeTooltip || !isHoveringCircle) return;

  const mouseX = event.clientX;
  const mouseY = event.clientY - CONFIG.MOUSE_OFFSET_Y;

  // Smooth follow animation
  gsap.to(activeTooltip, {
    left: mouseX,
    top: mouseY,
    duration: CONFIG.FOLLOW_DURATION,
    ease: "power2.out",
  });
}

/**
 * Handle mouse leave on circle - hide tooltip
 */
function handleCircleMouseLeave(event) {
  const circle = event.target;
  const circleId = circle.dataset.id;

  if (!circleId) return;

  const tooltip = document.querySelector(`[data-tooltip][data-id="${circleId}"]`);
  if (!tooltip) return;

  // Clear any pending show timeout
  if (showTooltipTimeout) {
    clearTimeout(showTooltipTimeout);
    showTooltipTimeout = null;
  }

  // Clear active tooltip tracking only if leaving the current active circle
  if (activeCircleId === circleId) {
    isHoveringCircle = false;
    activeCircleId = null;
  }

  // Small delay before hiding to prevent flicker when moving between adjacent circles
  hideTooltipTimeout = setTimeout(() => {
    // Only hide if we're not hovering another circle and this is still the active tooltip
    if (!isHoveringCircle && activeTooltip === tooltip) {
      gsap.to(tooltip, {
        opacity: 0,
        // scale: 0.8,
        duration: CONFIG.ANIMATION_DURATION * 0.7,
        ease: "power2.in",
        onComplete: () => {
          if (activeTooltip === tooltip) activeTooltip = null;
        },
      });
    }
  }, CONFIG.TOOLTIP_HIDE_DELAY);
}

// ———————————————————————————————————————————— ASSETS ———————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

const logArgs = { eventName: "init_circles", inline: true, disabled: false };
const msgs = {
  start: ["init. circles"],
  end: ["init. circles", "init. success"],
  no_el: ["init. circles", "no valid DOM el. provided"],
};

// —————————————————————————————————————— INIT. METHOD DEF. ——————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

export default function init_circles() {
  //////////////////////////////////////////// Setup /////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////

  const { circles, toolTips } = this.ref;
  this.logger("init", msgs.start, "action", logArgs);
  if (circles === undefined) return this.cancel_featInit(msgs.no_el);
  if (circles.every((el) => !this.validate_refEl(el))) return this.cancel_featInit(msgs.no_el);

  //////////////////////////////////////////// ACTION ////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////

  // Add global mouse move listener for smooth tooltip following
  document.addEventListener("mousemove", handleGlobalMouseMove);

  // Initialize tooltip positioning for each circle
  circles.forEach((c) => {
    c.addEventListener("mouseenter", handleCircleMouseEnter);
    c.addEventListener("mouseleave", handleCircleMouseLeave);
  });

  // Initialize tooltips (ensure they're positioned absolute/fixed)
  if (toolTips && toolTips.length > 0) {
    // Set initial tooltip styles
    toolTips.forEach((tt) => {
      gsap.set(tt, { position: "fixed", opacity: 0, pointerEvents: "none", zIndex: 1000 });
    });
  }

  /////////////////////////////////////////// Conclude ///////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////

  this.logger("init", msgs.end, "success", logArgs);
}

// ———————————————————————————————————————————————————————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //
